import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { AppRoutes } from "@/constants/routes";
import { useRoles } from "@/hooks/useRoles";
import { DollarData } from "@/pages/config/price-board/schema/price-board.schema";
import { getDollarSection } from "@/services/price-board.service";
import { zodResolver } from "@hookform/resolvers/zod";
import { Flex } from "@chakra-ui/react";
import dayjs from "dayjs";
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ZonalGrossMarginFormMemo } from "../components/ZonalGrossMarginForm";
import {
  calculateImplantationCost,
  calculateInvestmentTotal,
  getDefaultZonalGrossMarginFormValues,
} from "../utils/zonal-gross-margin-converters";
import { hasEmptyZonalGrossMarginFormValues } from "../utils/zonal-gross-margin-form";
import { createZonalGrossMarginHandler } from "./schema/handler-functions";
import { fetchZonalGrossMarginOptionsHandler } from "../schema/options-handler-functions";
import {
  ZonalGrossMarginCreationForm,
  zonalGrossMarginCreationSchema,
} from "./schema/zonal-gross-margin-creation.schema";

export const ZonalGrossMarginCreationPage = () => {
  const navigate = useNavigate();
  const { fetchPermissions } = useRoles();

  const [isActive, setIsActive] = useState(false);
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [summaryValues, setSummaryValues] = useState<ZonalGrossMarginCreationForm | null>(null);
  const [latestDollarValue, setLatestDollarValue] = useState<number | null>(null);
  const [zones, setZones] = useState<any[]>([]);
  const [grains, setGrains] = useState<any[]>([]);
  const [campaigns, setCampaigns] = useState<any[]>([]);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const fetchOptions = async () => {
    setLoadingOptions(true);
    try {
      const [options, dollarResponse] = await Promise.all([
        fetchZonalGrossMarginOptionsHandler({ applyPPMBFilterToGrains: true }),
        fetchLatestDollarQuotation(),
      ]);

      setZones(options.zones);
      setGrains(options.grains);
      setCampaigns(options.campaigns);
      if (dollarResponse) {
        setLatestDollarValue(dollarResponse);
      }
    } finally {
      setLoadingOptions(false);
    }
  };

  useEffect(() => {
    if (isActive) {
      fetchOptions();
    }
  }, [isActive]);

  const fetchLatestDollarQuotation = async () => {
    const today = dayjs();
    const response = (await getDollarSection({
      fechaDesde: today.subtract(60, "day").format("YYYY-MM-DD"),
      fechaHasta: today.format("YYYY-MM-DD"),
    })) as DollarData | null;

    const latestDollar = [...(response?.dolarDivisa ?? [])]
      .sort(
        (a, b) =>
          dayjs(b.fecha).valueOf() - dayjs(a.fecha).valueOf(),
      )[0];

    return latestDollar?.venta;
  };

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm<ZonalGrossMarginCreationForm>({
    resolver: zodResolver(zonalGrossMarginCreationSchema),
    defaultValues: getDefaultZonalGrossMarginFormValues(),
  });

  const watchedValues = useWatch({ control });
  const prevInvestmentTotal = useRef<string>("");

  useEffect(() => {
    if (latestDollarValue) {
      reset({
        idZonaProductiva: [],
        idGranoProductivo: [],
        idCampanaProductiva: [],
        cotizacionDolar: String(latestDollarValue),
        precioCereal: "",
        rindeEstimadoZonal: "",
        precioAgroquimicos: "",
        precioFertilizantes: "",
        precioSemillas: "",
        precioLabores: "",
        precioSeguros: "",
        precioAlquiler: "",
        gastoDeCosecha: "",
        inversionTotal: "",
        equilibrio: "",
      });
    }
  }, [latestDollarValue, reset]);

  useEffect(() => {
    if (!isActive) return;

    const fillDollarValue = async () => {
      const latestDollarValue = await fetchLatestDollarQuotation();
      if (!latestDollarValue) return;
      if (String(getValues("cotizacionDolar") ?? "").trim() !== "") return;

      setValue("cotizacionDolar", String(latestDollarValue), {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: true,
      });
    };

    fillDollarValue();
  }, [isActive]);
  
  const selectedGrainId = useWatch({ control, name: "idGranoProductivo" });

  useEffect(() => {
    const grainId = Number((selectedGrainId ?? [])[0]);
    if (!grainId) return;

    const selectedGrain = grains.find((grain) => grain.id === grainId);
    const cerealPrice = Number(selectedGrain?.precioPizarra ?? 0);
    if (!cerealPrice) return;

    setValue("precioCereal", String((cerealPrice / 10).toFixed(2)), {
      shouldDirty: true,
      shouldTouch: false,
      shouldValidate: false, // avoid triggering a state update cycle
    });
  }, [grains, selectedGrainId]);

  const implantationCost = useMemo(() => {
    return calculateImplantationCost(watchedValues);
  }, [
    watchedValues.precioAgroquimicos,
    watchedValues.precioFertilizantes,
    watchedValues.precioLabores,
    watchedValues.precioSeguros,
    watchedValues.precioSemillas,
  ]);

  const investmentTotal = useMemo(() => {
    return calculateInvestmentTotal(watchedValues);
  }, [
    watchedValues.precioAgroquimicos,
    watchedValues.precioFertilizantes,
    watchedValues.precioLabores,
    watchedValues.precioSeguros,
    watchedValues.precioSemillas,
    watchedValues.precioAlquiler,
  ]);

  const prevImplantationCost = useRef<string>("");

  useEffect(() => {
    const nextValue = String(Number(implantationCost.toFixed(2)));
    if (prevImplantationCost.current === nextValue) return;

    prevImplantationCost.current = nextValue;
    setValue("costoImplantacion", nextValue, {
      shouldDirty: true,
      shouldTouch: false,
      shouldValidate: false,
    });
  }, [implantationCost, setValue]);

  useEffect(() => {
    const nextValue = String(Number(investmentTotal.toFixed(2)));
    if (prevInvestmentTotal.current === nextValue) return;

    prevInvestmentTotal.current = nextValue;
    setValue("inversionTotal", nextValue, {
      shouldDirty: true,
      shouldTouch: false,
      shouldValidate: false,
    });
  }, [investmentTotal, setValue]);

  const hasNoFilledValues = useMemo(() => {
    return hasEmptyZonalGrossMarginFormValues(watchedValues);
  }, [watchedValues]);

  const selectedGrainName = useMemo(() => {
    const selectedGrainId = Number((summaryValues?.idGranoProductivo ?? [])[0]);
    return grains.find((grain) => grain.id === selectedGrainId)?.nombre ?? "-";
  }, [grains, summaryValues?.idGranoProductivo]);

  const handleCancel = () => {
    navigate(AppRoutes.ZONAL_GROSS_MARGINS);
  };

  const handleOpenConfirm = async (values: ZonalGrossMarginCreationForm) => {
    setSummaryValues(values);
    setOpenConfirm(true);
  };

  const onSubmit = async (values: ZonalGrossMarginCreationForm) => {
    setOpenConfirm(false);
    await createZonalGrossMarginHandler({
      values,
      setLoadingCreate,
      navigate,
      fetchPermissions,
    });
  };

  const handleConfirmAccept = async () => {
    if (!summaryValues) return;
    await onSubmit(summaryValues);
  };

  return (
    <Flex
      flexDir="column"
      paddingTop="20px"
      gap="12px"
      data-testid="zonal-gross-margin-creation-page"
      overflowY="auto"
      maxH="calc(100vh - 32px)"
      paddingRight="4px"
    >
      <SectionHeader
        title="Agregar Margen Bruto Zonal"
        addButtonLabel="Completar y enviar"
        cancelButtonLabel="Cancelar"
        handleCancel={handleCancel}
        handleAdd={handleSubmit(handleOpenConfirm)}
        addButtonType="button"
        disableAdd={hasNoFilledValues || loadingCreate || loadingOptions}
        loading={loadingCreate}
      />

      <Flex as="form" onSubmit={handleSubmit(handleOpenConfirm)} flexDir="column">
        <ZonalGrossMarginFormMemo
          control={control}
          errors={errors}
          zones={zones}
          grains={grains}
          campaigns={campaigns}
          isActive={isActive}
          loading={loadingCreate}
          loadingOptions={loadingOptions}
        />
      </Flex>

      <Suspense>
        <ZonalGrossMarginConfirmModalDynamic
          open={openConfirm}
          setOpen={setOpenConfirm}
          data={summaryValues}
          onAccept={handleConfirmAccept}
          loading={loadingCreate}
          grainName={selectedGrainName}
        />
      </Suspense>
    </Flex>
  );
};


const ZonalGrossMarginConfirmModalDynamic =
  lazy(() => import("../components/ZonalGrossMarginConfirmationModal").then(module => ({ default: module.ZonalGrossMarginConfirmationModal })))
